// Classifies a multi-SKU Add-to-Cart (GIS ATB) response into one of three
// outcomes so AddAllButton can route to FER-797 (full success),
// FER-755 (partial failure) or FER-798 (total failure).
//
// The Cart API signals an unavailable item in more than one way, so a requested
// SKU is treated as failed when ANY of these hold:
//   1. it is flagged on the `errors` map (keyed by skuId), or
//   2. it is absent from `response.items` — the list of what actually landed in
//      the basket (the API can drop an unavailable SKU with no `errors` entry).
// In addition, the response carries a basket-level out-of-stock warning
// (`basketLevelMessages[].messageContext === 'basket.inventoryNotAvailable'`);
// this is the authoritative "one or more items unavailable" flag and is honored
// even when `items` returns the full basket (so an already-in-basket SKU that
// just went out of stock cannot be missed by the items-absence check alone).
//
// When the response reports no per-item skuIds at all (items omit `sku.skuId`),
// absence cannot be inferred and only the `errors`/warning signals are used.
//
// A rejected promise (Cart API error) is handled by the caller, not here.

const ATB_RESULT = {
    SUCCESS: 'SUCCESS',
    PARTIAL: 'PARTIAL',
    ERROR: 'ERROR'
};

const INVENTORY_UNAVAILABLE_CONTEXT = 'basket.inventoryNotAvailable';

const hasInventoryUnavailableWarning = response =>
    (response?.basketLevelMessages || []).some(message => message?.messageContext === INVENTORY_UNAVAILABLE_CONTEXT);

/**
 * @param {string[]} requestedSkuIds - SKU ids submitted in the ATB request.
 * @param {object} response - Cart API basket response.
 * @returns {'SUCCESS'|'PARTIAL'|'ERROR'}
 */
function classifyAtbResult(requestedSkuIds = [], response = {}) {
    const errors = response?.errors || {};
    const addedSkuIds = new Set((response?.items || []).map(item => item?.sku?.skuId).filter(Boolean));
    const canInferFromItems = addedSkuIds.size > 0;

    const failedSkuIds = requestedSkuIds.filter(skuId => Boolean(errors[skuId]) || (canInferFromItems && !addedSkuIds.has(skuId)));
    const addedCount = requestedSkuIds.length - failedSkuIds.length;

    if (addedCount <= 0) {
        return ATB_RESULT.ERROR;
    }

    if (failedSkuIds.length > 0 || hasInventoryUnavailableWarning(response)) {
        return ATB_RESULT.PARTIAL;
    }

    return ATB_RESULT.SUCCESS;
}

export { ATB_RESULT };

export default classifyAtbResult;
